import React from 'react';

import { statFormatter, hockeyFormatter, stringFormatter } from '@nhl/formatter-utilities';

import GlobalColumnFunctions from '../../../../../../../utils/globalColumnFunctions';
import BaseColumnDefinitions from '../baseColumnDefinitions';

import { goaltenderInternationalColumns } from './goaltenderInternationalColumns';

// Shortening the function name
const CreateHeaderCell = BaseColumnDefinitions.CreateHeaderCell;
const setMinWidth = (cols, width) => cols.map((col) => ({ ...col, minWidth: width }));

// Common cells
const DURATION_SECONDS = {
    accessor: 'durationSeconds',
    Header: CreateHeaderCell('Duration'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ original }) => <div>{original.durationMinSec}</div>,
};
const NUMBER_OF_GAMES_IN_SEASON = {
    accessor: 'numberOfGamesInSeason',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Games In Season'),
};
export const SAVES = {
    accessor: 'saves',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Saves', 'Sv'),
};
const UNDEFEATED = {
    accessor: 'undefeatedStreak',
    Header: CreateHeaderCell('Undefeated Streak'),
    getProps: GlobalColumnFunctions.getProps,
};
export const GOALS_AGAINST_AVERAGE_COL = {
    accessor: 'goalsAgainstAverage',
    Header: CreateHeaderCell('Goals Against Average', 'GAA'),
    Cell: ({ row, value }) => statFormatter.displayValueAs('goalsAgainstAverage')(value, row.original.shotsAgainst > 0),
    getProps: GlobalColumnFunctions.getProps,
    sortType: 'basic',
};
export const SAVE_PCTG_COL = {
    accessor: 'savePctg',
    Header: CreateHeaderCell('Save %', 'Sv%'),
    Cell: ({ value }) => statFormatter.displayValueAs('savePctg')(value),
    getProps: GlobalColumnFunctions.getProps,
    sortType: 'basic',
};
const RESULT_COL = {
    accessor: 'result',
    Header: CreateHeaderCell('Result'),
    getProps: GlobalColumnFunctions.getProps,
};
const SHUTOUT_STREAK_COL = {
    accessor: 'shutoutGamesStreak',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Shutout Streak'),
};

const POINT_STREAK_COL = {
    accessor: 'pointStreak',
    Header: CreateHeaderCell('Streak Length'),
    getProps: GlobalColumnFunctions.getProps,
};

// Common primary sorts
const getBaseGoaltenderColumns = (includeOvertimeLosses) =>
    includeOvertimeLosses
        ? [
              ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
              BaseColumnDefinitions.OVERTIME_LOSSES_COL,
              BaseColumnDefinitions.SHUTOUTS_COL,
              BaseColumnDefinitions.TIME_ON_ICE_COL,
          ]
        : BASE;
const BASE = [
    ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
    BaseColumnDefinitions.SHUTOUTS_COL,
    BaseColumnDefinitions.TIME_ON_ICE_COL,
];
const CONSECUTIVE_BASE_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.PLAYER_COL,
    BaseColumnDefinitions.SEASONS_PLAYED_COL,
    BaseColumnDefinitions.TEAM_ABBREVS,
];
const CONSECUTIVE_TIME_COLS = [
    {
        accessor: 'streakStartSeason',
        Header: CreateHeaderCell('Starting Season of Streak', 'Starting Season'),
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => (value ? hockeyFormatter.singleSeasonFormatter(value) : ''),
    },
    {
        accessor: 'streakEndSeason',
        Header: CreateHeaderCell('Ending Season of Streak', 'Ending Season'),
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => (value ? hockeyFormatter.singleSeasonFormatter(value) : ''),
    },
];
const MOST_X_OR_MORE_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.PLAYER_COL,
    BaseColumnDefinitions.SEASONS_PLAYED_COL,
    BaseColumnDefinitions.TEAM_ABBREVS,
    {
        accessor: 'twentyWinSeasons',
        Header: CreateHeaderCell('Twenty Win Seasons', '20W'),
        getProps: GlobalColumnFunctions.getProps,
    },
    {
        accessor: 'thirtyWinSeasons',
        Header: CreateHeaderCell('Thirty Win Seasons', '30W'),
        getProps: GlobalColumnFunctions.getProps,
    },
    {
        accessor: 'fortyWinSeasons',
        Header: CreateHeaderCell('Forty Win Seasons', '40W'),
        getProps: GlobalColumnFunctions.getProps,
    },
];
const SHOOTOUT = [
    BaseColumnDefinitions.WINS_COL,
    BaseColumnDefinitions.LOSSES_COL,
    BaseColumnDefinitions.SHOTS_AGAINST_COL,
    SAVES,
    {
        accessor: 'goalsAllowed',
        Header: CreateHeaderCell('Goals Allowed', 'GA'),
        getProps: GlobalColumnFunctions.getProps,
    },
    SAVE_PCTG_COL,
];
const SHOOTOUT_ONE_SEASON_BASE = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.PLAYER_COL,
    BaseColumnDefinitions.SEASON_COL,
    BaseColumnDefinitions.TEAMS_COL,
];
const STANLEY_CUP_WINS = {
    accessor: 'stanleyCupWins',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Cup Wins'),
};
const SAVE_PCTG_BASE_WITHOUT_GAA = [
    BaseColumnDefinitions.GOALS_AGAINST_COL,
    SAVES,
    BaseColumnDefinitions.SHOTS_AGAINST_COL,
    SAVE_PCTG_COL,
];
const SAVE_PCTG_BASE = [...SAVE_PCTG_BASE_WITHOUT_GAA, GOALS_AGAINST_AVERAGE_COL];
const GAA_BASE = [
    BaseColumnDefinitions.GOALS_AGAINST_COL,
    BaseColumnDefinitions.TIME_ON_ICE_COL,
    GOALS_AGAINST_AVERAGE_COL,
    SAVE_PCTG_COL,
];
const SAVES_GOALS_AGAINST_BASE = [
    SAVES,
    SAVE_PCTG_COL,
    BaseColumnDefinitions.GOALS_AGAINST_COL,
    GOALS_AGAINST_AVERAGE_COL,
];

const OT_SHOTS_AGAINST = {
    accessor: 'overtimeShotsAgainst',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Shots Against', 'OT SA'),
    sortDescFirst: true,
};

const OT_SAVES = {
    accessor: 'overtimeSaves',
    Header: CreateHeaderCell('Overtime Saves', 'OT SV'),
    Cell: ({ row }) => {
        const shotsAgainst = row.original.overtimeShotsAgainst;
        const goalsAgainst = row.original.overtimeGoalsAgainst;
        return stringFormatter.cellDisplay(shotsAgainst - goalsAgainst);
    },
    getProps: GlobalColumnFunctions.getProps,
    sortType: 'basic',
};

const OT_SAVE_PERCENTAGE = {
    accessor: 'overtimeSavePctg',
    Header: CreateHeaderCell('Overtime Save Percentage', 'OT Sv%'),
    Cell: ({ value }) => statFormatter.displayValueAs('savePctg')(value),
    getProps: GlobalColumnFunctions.getProps,
    sortType: 'basic',
};

const OT_SAVE_PERCENTAGE_GAME = {
    accessor: 'overtimeSavePctgGame',
    Header: CreateHeaderCell('Overtime Save Percentage', 'OT Sv%'),
    Cell: (cellData) => {
        const shotsAgainst = cellData.row.original.overtimeShotsAgainst;
        const goalsAgainst = cellData.row.original.overtimeGoalsAgainst;
        return statFormatter.displayValueAs('savePctg')((shotsAgainst - goalsAgainst) / shotsAgainst);
    },
    getProps: GlobalColumnFunctions.getProps,
    sortType: 'basic',
};

const OT_GOALS_AGAINST = {
    accessor: 'overtimeGoalsAgainst',
    Header: CreateHeaderCell('Overtime Goals Against', 'OT GA'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    sortDescFirst: true,
};

const OT_GAA = {
    accessor: 'overtimeGoalsAgainstAverage',
    Header: CreateHeaderCell('Overtime Goals Against Average', 'OT GAA'),
    Cell: ({ value }) => statFormatter.displayValueAs('goalsAgainstAverage')(value),
    getProps: GlobalColumnFunctions.getProps,
    sortType: 'basic',
};

const GAME_SEVEN_PLAYOFF_GAMES = [
    {
        accessor: 'gameSevenGamesPlayed',
        Header: CreateHeaderCell('Game Seven Games Played', 'G7 GP'),
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
    },
    {
        accessor: 'gameSevenWins',
        Header: CreateHeaderCell('Game Seven Wins', 'G7 W'),
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => stringFormatter.cellDisplay(value) || 0,
    },
    {
        accessor: 'gameSevenLosses',
        Header: CreateHeaderCell('Game Seven Losses', 'G7 L'),
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => (value ? stringFormatter.cellDisplay(value) : 0),
    },
    {
        accessor: 'gameSevenWinPctg',
        Header: CreateHeaderCell('Game Seven Win Percentage', 'G7 W%'),
        getProps: GlobalColumnFunctions.getProps,
        Cell: (cellData) => {
            const games = cellData.row.values.gameSevenGamesPlayed;
            const wins = cellData.row.values.gameSevenWins || 0;
            return statFormatter.displayValueAs('pointPctg')(wins / games);
        },
    },
];

const GOALIE_ELIMINATION_GAME_TOTALS_COLS = [
    ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
    BaseColumnDefinitions.GAMES_PLAYED_COL,
    ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
    BaseColumnDefinitions.SHUTOUTS_COL,
    ...SAVES_GOALS_AGAINST_BASE,
];
const GOALIE_ELIMINATION_GAME_SEASON_COLS = [
    ...BaseColumnDefinitions.PLAYER_TEAM_BASE_COLS,
    BaseColumnDefinitions.SEASON_COL,
    BaseColumnDefinitions.GAMES_PLAYED_COL,
    ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
    BaseColumnDefinitions.SHUTOUTS_COL,
    ...SAVES_GOALS_AGAINST_BASE,
];
const GOALIE_ELIMINATION_GAME_SERIES_COLS = [
    ...BaseColumnDefinitions.PLAYER_TEAM_BASE_COLS,
    BaseColumnDefinitions.SEASON_COL,
    { ...BaseColumnDefinitions.OPPONENT_COL, accessor: 'opponentName' },
    BaseColumnDefinitions.SERIES_TITLE,
    BaseColumnDefinitions.GAMES_PLAYED_COL,
    ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
    SAVES,
    BaseColumnDefinitions.SHOTS_AGAINST_COL,
];
const GOALIE_ELIMINATION_GAME_SHUTOUT_COLS = [
    ...BaseColumnDefinitions.PLAYER_TEAM_BASE_COLS,
    { ...BaseColumnDefinitions.OPPONENT_COL, accessor: 'opponentName' },
    BaseColumnDefinitions.GAME_DATE_COL,
    ...BaseColumnDefinitions.SERIES_GAME_COLS,
    BaseColumnDefinitions.GAME_SCORE_COL,
    RESULT_COL,
    SAVES,
];
const GOALIE_ELIMINATION_GAME_COLS = [...GOALIE_ELIMINATION_GAME_SHUTOUT_COLS, BaseColumnDefinitions.SHOTS_AGAINST_COL];
const GOALIE_POINT_STREAK_COLS = [
    ...BaseColumnDefinitions.PLAYER_TEAM_BASE_COLS,
    BaseColumnDefinitions.SEASON_COL,
    POINT_STREAK_COL,
    BaseColumnDefinitions.WINS_COL,
    BaseColumnDefinitions.OVERTIME_LOSSES_COL,
    BaseColumnDefinitions.TIES_COL,
    ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
];
const GOALIE_SCF_SERIES_MODIFIED_WIDTH_COLS = [
    { ...BaseColumnDefinitions.GAMES_PLAYED_COL, minWidth: 75 },
    ...setMinWidth(BaseColumnDefinitions.WIN_LOSS_TIE_COLS, 75),
    { ...BaseColumnDefinitions.SHOTS_AGAINST_COL, minWidth: 75 },
    { ...SAVES, minWidth: 75 },
    { ...BaseColumnDefinitions.GOALS_AGAINST_COL, minWidth: 75 },
];

export const goaltenderColumnDefinitions = {
    // Games Played
    'most-games-played-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...getBaseGoaltenderColumns(true)],
    'most-games-played-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...getBaseGoaltenderColumns(true)],
    'most-consecutive-games-played-career': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        {
            accessor: 'streak',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Streak'),
        },
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],

    // Games Played - playoffs
    'goalie-most-seasons-playoff': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        {
            accessor: 'seasonsPlayed',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Playoff Seasons'),
        },
        STANLEY_CUP_WINS,
    ],
    'goalie-most-consecutive-seasons-playoff': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        ...BaseColumnDefinitions.START_END_SEASON_COLS,
        {
            accessor: 'consecutivePlayoffSeasons',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Consecutive Seasons'),
        },
        STANLEY_CUP_WINS,
    ],
    'most-games-played-career-playoff': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE],
    'most-games-played-one-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE],

    // Minutes Played
    'most-minutes-played-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...getBaseGoaltenderColumns(true)],
    'most-minutes-played-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, NUMBER_OF_GAMES_IN_SEASON, ...getBaseGoaltenderColumns(true)],

    // Minutes Played - playoffs
    'most-minutes-played-career-playoff': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE],
    'most-minutes-played-one-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE],

    // Wins
    'most-wins-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...getBaseGoaltenderColumns(true)],
    'most-wins-career-including-playoffs': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        ...getBaseGoaltenderColumns(true),
    ],
    'goalie-most-wins-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, NUMBER_OF_GAMES_IN_SEASON, ...getBaseGoaltenderColumns(true)],
    'most-wins-one-season-including-playoffs': [
        ...BaseColumnDefinitions.PLAYER_SEASON_COLS,
        ...getBaseGoaltenderColumns(true),
    ],
    'longest-winning-streak-one-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        BaseColumnDefinitions.WIN_STREAK_COL,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],
    'longest-home-winning-streaks-one-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        BaseColumnDefinitions.WIN_STREAK_COL,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],
    'longest-road-winning-streaks-one-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        BaseColumnDefinitions.WIN_STREAK_COL,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],
    'longest-winning-streaks-rookie-one-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        BaseColumnDefinitions.WIN_STREAK_COL,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],
    'longest-home-winning-streaks-rookie-one-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        BaseColumnDefinitions.WIN_STREAK_COL,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],
    'longest-road-winning-streaks-rookie-one-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        BaseColumnDefinitions.WIN_STREAK_COL,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],
    'goalie-longest-undefeated-streak-one-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        UNDEFEATED,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],
    'longest-undefeated-streak-first-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        UNDEFEATED,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],
    'longest-undefeated-streak-career-start': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        UNDEFEATED,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],
    'most-30-or-more-win-seasons': MOST_X_OR_MORE_COLS,
    'most-40-or-more-win-seasons': MOST_X_OR_MORE_COLS,
    'most-consecutive-30-or-more-win-seasons': [
        ...CONSECUTIVE_BASE_COLS,
        {
            accessor: 'consecutiveThirtyWinSeasons',
            Header: CreateHeaderCell('Consecutive 30-or-More Win Seasons', '30+ Win Seasons'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...CONSECUTIVE_TIME_COLS,
    ],
    'most-consecutive-40-or-more-win-seasons': [
        ...CONSECUTIVE_BASE_COLS,
        {
            accessor: 'consecutiveFortyWinSeasons',
            Header: CreateHeaderCell('Consecutive 40-or-More Win Seasons', '40+ Win Seasons'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...CONSECUTIVE_TIME_COLS,
    ],
    'longest-winning-streak-multiple-playoff-years': [
        ...BaseColumnDefinitions.PLAYER_TEAM_BASE_COLS,
        BaseColumnDefinitions.WIN_STREAK_COL,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],
    'longest-winning-streaks-playoff': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        BaseColumnDefinitions.WIN_STREAK_COL,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],

    // Wins - playoffs
    'most-wins-career-playoff': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE],
    'goalie-most-wins-one-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE],

    // Shutouts
    'most-shutouts-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        BaseColumnDefinitions.WINS_COL,
        BaseColumnDefinitions.LOSSES_COL,
        BaseColumnDefinitions.TIES_COL,
        BaseColumnDefinitions.OVERTIME_LOSSES_COL,
        GOALS_AGAINST_AVERAGE_COL,
        SAVE_PCTG_COL,
        BaseColumnDefinitions.SHUTOUTS_COL,
    ],
    'goalie-most-shutouts-one-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_COLS,
        BaseColumnDefinitions.WINS_COL,
        BaseColumnDefinitions.LOSSES_COL,
        BaseColumnDefinitions.TIES_COL,
        BaseColumnDefinitions.OVERTIME_LOSSES_COL,
        GOALS_AGAINST_AVERAGE_COL,
        SAVE_PCTG_COL,
        BaseColumnDefinitions.SHUTOUTS_COL,
    ],
    'longest-shutout-sequence': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        DURATION_SECONDS,
        SAVES,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],

    // Shutouts - playoffs
    'most-shutouts-career-playoff': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE],
    'most-shutouts-one-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE],
    'longest-shutout-sequence-playoff': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        DURATION_SECONDS,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],
    'most-shutouts-one-series-playoff': [
        ...BaseColumnDefinitions.PLAYER_SEASON_WITHOUT_GP_COLS,
        BaseColumnDefinitions.OPPONENT_COL,
        BaseColumnDefinitions.SERIES_TITLE,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.SHUTOUTS_COL,
        SAVES,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
    ],
    'most-consecutive-shutouts-playoff': [
        ...BaseColumnDefinitions.PLAYER_SEASON_WITHOUT_GP_COLS,
        {
            accessor: 'shutoutGamesStreak',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Shutout Games Streak', 'SO Streak'),
        },
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],
    'most-consecutive-shutouts-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        {
            accessor: 'shutoutGamesStreak',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Shutout Games Streak', 'SO Streak'),
        },
        SAVES,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],

    // Ties
    'most-ties-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE],
    'goalie-most-ties-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, NUMBER_OF_GAMES_IN_SEASON, ...BASE],

    // Losses
    'most-losses-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...getBaseGoaltenderColumns(true)],
    'most-losses-career-including-playoffs': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        ...getBaseGoaltenderColumns(true),
    ],
    'goalie-most-losses-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, NUMBER_OF_GAMES_IN_SEASON, ...getBaseGoaltenderColumns(true)],

    // Points
    'most-points-goaltender-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        BaseColumnDefinitions.SEASONS_PLAYED_COL,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'most-points-goaltender-one-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'most-points-goaltender-one-game': [
        ...BaseColumnDefinitions.PLAYER_TEAM_BASE_COLS,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SCORE_COL,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],

    //Point Streaks
    'longest-point-streaks-season': GOALIE_POINT_STREAK_COLS,

    'longest-home-point-streak-season': GOALIE_POINT_STREAK_COLS,

    'longest-road-point-streak-season': GOALIE_POINT_STREAK_COLS,

    'longest-point-streaks-rookie-season': GOALIE_POINT_STREAK_COLS,

    'longest-home-point-streaks-rookie-season': GOALIE_POINT_STREAK_COLS,

    'longest-road-point-streaks-rookie-season': GOALIE_POINT_STREAK_COLS,

    // Shootout
    'most-shootout-wins-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS, ...SHOOTOUT],
    'goalie-most-shootout-wins-one-season': [...SHOOTOUT_ONE_SEASON_BASE, ...SHOOTOUT],
    'most-shootout-shots-against-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS, ...SHOOTOUT],
    'most-shootout-shots-against-one-season': [...SHOOTOUT_ONE_SEASON_BASE, ...SHOOTOUT],
    'best-shootout-save-percentage-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS, ...SHOOTOUT],
    'best-shootout-save-percentage-one-season': [...SHOOTOUT_ONE_SEASON_BASE, ...SHOOTOUT],

    //Save PCTG
    'highest-save-percentage-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...SAVE_PCTG_BASE],
    'highest-save-percentage-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        BaseColumnDefinitions.SEASONS_PLAYED_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        ...SAVE_PCTG_BASE,
    ],
    'highest-save-percentage-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...SAVE_PCTG_BASE],
    'highest-save-percentage-career-playoff': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        BaseColumnDefinitions.SEASONS_PLAYED_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        ...SAVE_PCTG_BASE,
    ],

    //GAA
    'lowest-goals-against-average-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...GAA_BASE],
    'lowest-goals-against-average-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        BaseColumnDefinitions.SEASONS_PLAYED_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        ...GAA_BASE,
    ],
    'lowest-goals-against-average-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...GAA_BASE],
    'lowest-goals-against-average-career-playoff': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        BaseColumnDefinitions.SEASONS_PLAYED_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        ...GAA_BASE,
    ],

    //Saves
    'most-saves-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...SAVE_PCTG_BASE],
    'most-saves-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        BaseColumnDefinitions.SEASONS_PLAYED_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        ...SAVE_PCTG_BASE,
    ],
    'most-saves-game': [
        ...BaseColumnDefinitions.GOALIE_GAME_COLS,
        BaseColumnDefinitions.SCORE_COL,
        ...SAVE_PCTG_BASE_WITHOUT_GAA,
    ],
    'most-saves-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...SAVE_PCTG_BASE],
    'most-saves-career-playoff': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        BaseColumnDefinitions.SEASONS_PLAYED_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        ...SAVE_PCTG_BASE,
    ],
    'most-saves-game-playoff': [
        ...BaseColumnDefinitions.GOALIE_GAME_COLS,
        BaseColumnDefinitions.SCORE_COL,
        ...SAVE_PCTG_BASE_WITHOUT_GAA,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],

    //Shots Against
    'most-shots-against-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...SAVE_PCTG_BASE],
    'most-shots-against-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        BaseColumnDefinitions.SEASONS_PLAYED_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        ...SAVE_PCTG_BASE,
    ],
    'most-shots-against-game': [
        ...BaseColumnDefinitions.GOALIE_GAME_COLS,
        BaseColumnDefinitions.SCORE_COL,
        ...SAVE_PCTG_BASE_WITHOUT_GAA,
    ],
    'most-shots-against-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...SAVE_PCTG_BASE],
    'most-shots-against-career-playoff': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        BaseColumnDefinitions.SEASONS_PLAYED_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        ...SAVE_PCTG_BASE,
    ],
    'most-shots-against-game-playoff': [
        ...BaseColumnDefinitions.GOALIE_GAME_COLS,
        BaseColumnDefinitions.SCORE_COL,
        ...SAVE_PCTG_BASE_WITHOUT_GAA,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],

    //Youngest Players
    'youngest-goaltenders-first-nhl-win': [
        ...BaseColumnDefinitions.YOUNGEST_BASE_COLS,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.SCORE_COL,
        {
            ...BaseColumnDefinitions.AGE_COLUMN,
            minWidth: 120,
        },
        BaseColumnDefinitions.GOALS_AGAINST_COL,
        {
            accessor: 'saves',
            Header: CreateHeaderCell('Saves'),
            Cell: ({ value }) => stringFormatter.cellDisplay(value).toLocaleString(),
            getProps: GlobalColumnFunctions.getProps,
        },
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
        SAVE_PCTG_COL,
    ],
    'youngest-goaltenders-first-nhl-shutout': [
        ...BaseColumnDefinitions.YOUNGEST_BASE_COLS,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.SCORE_COL,
        {
            ...BaseColumnDefinitions.AGE_COLUMN,
            minWidth: 120,
        },
        {
            accessor: 'saves',
            Header: CreateHeaderCell('Saves'),
            Cell: ({ value }) => stringFormatter.cellDisplay(value).toLocaleString(),
            getProps: GlobalColumnFunctions.getProps,
        },
    ],
    // Stanley Cup Wins
    'goalie-most-stanley-cups-won-career': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        ...BaseColumnDefinitions.PLAYER_STANLEY_CUPS_COLS,
    ],
    'goalie-most-consecutive-stanley-cups-won': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        ...BaseColumnDefinitions.PLAYER_STANLEY_CUPS_COLS,
    ],
    'most-overtime-games-played-playoff-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        ...BaseColumnDefinitions.OT_RECORD_COLS,
    ],
    'most-overtime-wins-playoff-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        ...BaseColumnDefinitions.OT_RECORD_COLS,
    ],
    'most-overtime-losses-playoff-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        ...BaseColumnDefinitions.OT_RECORD_COLS,
    ],
    'most-overtime-wins-playoff-year': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        BaseColumnDefinitions.TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        ...BaseColumnDefinitions.OT_RECORD_COLS,
    ],
    'most-overtime-saves-playoff-game': [
        ...BaseColumnDefinitions.PLAYER_TEAM_BASE_COLS,
        { ...BaseColumnDefinitions.OPPONENT_COL, accessor: 'opponentName' },
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SERIES_TITLE,
        BaseColumnDefinitions.ROUND_NUMBER_COL,
        BaseColumnDefinitions.SERIES_GAME_NUMBER_COL,
        BaseColumnDefinitions.SCORE_COL,
        OT_SHOTS_AGAINST,
        OT_SAVES,
        OT_SAVE_PERCENTAGE_GAME,
    ],
    'highest-overtime-save-percentage-playoff-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        OT_GOALS_AGAINST,
        OT_SAVES,
        OT_SHOTS_AGAINST,
        OT_SAVE_PERCENTAGE,
        OT_GAA,
    ],
    'goalie-most-game-7-games-played-playoff-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        ...GAME_SEVEN_PLAYOFF_GAMES,
    ],
    'goalie-most-game-7-wins-playoff-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        ...GAME_SEVEN_PLAYOFF_GAMES,
    ],
    'goalie-most-game-7-losses-playoff-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        ...GAME_SEVEN_PLAYOFF_GAMES,
    ],
    'most-game-7-wins-playoff-year': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        BaseColumnDefinitions.TEAMS_COL,
        BaseColumnDefinitions.SEASON_COL,
        ...GAME_SEVEN_PLAYOFF_GAMES,
    ],
    'most-game-7-saves-playoff-game': [
        ...BaseColumnDefinitions.PLAYER_TEAM_BASE_COLS,
        { ...BaseColumnDefinitions.OPPONENT_COL, accessor: 'opponentName' },
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.SERIES_TITLE,
        BaseColumnDefinitions.ROUND_NUMBER_COL,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
        SAVES,
        SAVE_PCTG_COL,
    ],

    // Outdoor columns
    'outdoor-games-goalie-registry': [
        BaseColumnDefinitions.PLAYER_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.FIRST_OUTDOOR_SEASON_COL,
        BaseColumnDefinitions.LAST_OUTDOOR_SEASON_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
        BaseColumnDefinitions.OVERTIME_LOSSES_COL,
        BaseColumnDefinitions.SHUTOUTS_COL,
        GOALS_AGAINST_AVERAGE_COL,
        SAVE_PCTG_COL,
    ],
    'most-outdoor-games-played-career-goalie': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        BaseColumnDefinitions.WINS_COL,
        BaseColumnDefinitions.LOSSES_COL,
        BaseColumnDefinitions.OVERTIME_LOSSES_COL,
        BaseColumnDefinitions.SHUTOUTS_COL,
        BaseColumnDefinitions.TIME_ON_ICE_COL,
    ],
    'most-outdoor-wins-career-goalie': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        BaseColumnDefinitions.WINS_COL,
        BaseColumnDefinitions.LOSSES_COL,
        BaseColumnDefinitions.OVERTIME_LOSSES_COL,
        BaseColumnDefinitions.SHUTOUTS_COL,
        BaseColumnDefinitions.TIME_ON_ICE_COL,
    ],
    'most-outdoor-saves-career-goalie': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
        GOALS_AGAINST_AVERAGE_COL,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
        SAVES,
        SAVE_PCTG_COL,
    ],
    'most-outdoor-saves-game-goalie': [
        ...BaseColumnDefinitions.PLAYER_TEAM_BASE_COLS,
        BaseColumnDefinitions.OPPONENT_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
        SAVES,
    ],
    'most-outdoor-saves-period-goalie': [
        ...BaseColumnDefinitions.PLAYER_TEAM_BASE_COLS,
        BaseColumnDefinitions.OPPONENT_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
        SAVES,
    ],
    'most-outdoor-shots-against-career-goalie': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
        GOALS_AGAINST_AVERAGE_COL,
        SAVES,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
        SAVE_PCTG_COL,
    ],
    'most-outdoor-shots-against-game-goalie': [
        ...BaseColumnDefinitions.PLAYER_TEAM_BASE_COLS,
        BaseColumnDefinitions.OPPONENT_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
        SAVES,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
    ],
    'most-outdoor-shots-against-period-goalie': [
        ...BaseColumnDefinitions.PLAYER_TEAM_BASE_COLS,
        BaseColumnDefinitions.OPPONENT_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
        SAVES,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
    ],
    'most-outdoor-shutouts-career-goalie': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        BaseColumnDefinitions.WINS_COL,
        BaseColumnDefinitions.LOSSES_COL,
        BaseColumnDefinitions.OVERTIME_LOSSES_COL,
        BaseColumnDefinitions.SHUTOUTS_COL,
        BaseColumnDefinitions.TIME_ON_ICE_COL,
    ],
    'goalie-most-games-facing-elimination-career': GOALIE_ELIMINATION_GAME_TOTALS_COLS,
    'goalie-most-wins-facing-elimination-career': GOALIE_ELIMINATION_GAME_TOTALS_COLS,
    'goalie-save-pctg-facing-elimination-career': GOALIE_ELIMINATION_GAME_TOTALS_COLS,
    'goalie-most-saves-facing-elimination-career': GOALIE_ELIMINATION_GAME_TOTALS_COLS,
    'goalie-gaa-facing-elimination-career': GOALIE_ELIMINATION_GAME_TOTALS_COLS,
    'goalie-most-games-facing-elimination-season': GOALIE_ELIMINATION_GAME_SEASON_COLS,
    'goalie-most-wins-facing-elimination-season': GOALIE_ELIMINATION_GAME_SEASON_COLS,
    'goalie-save-pctg-facing-elimination-season': GOALIE_ELIMINATION_GAME_SEASON_COLS,
    'goalie-most-saves-facing-elimination-season': GOALIE_ELIMINATION_GAME_SEASON_COLS,
    'goalie-gaa-facing-elimination-season': GOALIE_ELIMINATION_GAME_SEASON_COLS,
    'goalie-most-games-facing-elimination-series': GOALIE_ELIMINATION_GAME_SERIES_COLS,
    'goalie-most-wins-facing-elimination-series': GOALIE_ELIMINATION_GAME_SERIES_COLS,
    'goalie-most-saves-facing-elimination-series': GOALIE_ELIMINATION_GAME_SERIES_COLS,
    'goalie-most-shots-against-facing-elimination-series': GOALIE_ELIMINATION_GAME_SERIES_COLS,
    'goalie-most-saves-facing-elimination-game': GOALIE_ELIMINATION_GAME_COLS,
    'goalie-most-shots-against-facing-elimination-game': GOALIE_ELIMINATION_GAME_COLS,
    'goalie-shutouts-facing-elimination-game': GOALIE_ELIMINATION_GAME_SHUTOUT_COLS,
    'goalie-most-clinching-games-career': GOALIE_ELIMINATION_GAME_TOTALS_COLS,
    'goalie-most-clinching-wins-career': GOALIE_ELIMINATION_GAME_TOTALS_COLS,
    'goalie-save-pctg-clinching-games-career': GOALIE_ELIMINATION_GAME_TOTALS_COLS,
    'goalie-most-saves-clinching-games-career': GOALIE_ELIMINATION_GAME_TOTALS_COLS,
    'goalie-gaa-clinching-games-career': GOALIE_ELIMINATION_GAME_TOTALS_COLS,
    'goalie-most-clinching-games-season': GOALIE_ELIMINATION_GAME_SEASON_COLS,
    'goalie-most-clinching-wins-season': GOALIE_ELIMINATION_GAME_SEASON_COLS,
    'goalie-save-pctg-clinching-games-season': GOALIE_ELIMINATION_GAME_SEASON_COLS,
    'goalie-most-saves-clinching-games-season': GOALIE_ELIMINATION_GAME_SEASON_COLS,
    'goalie-gaa-clinching-games-season': GOALIE_ELIMINATION_GAME_SEASON_COLS,
    'goalie-most-clinching-games-series': GOALIE_ELIMINATION_GAME_SERIES_COLS,
    'goalie-most-clinching-wins-series': GOALIE_ELIMINATION_GAME_SERIES_COLS,
    'goalie-most-saves-clinching-games-series': GOALIE_ELIMINATION_GAME_SERIES_COLS,
    'goalie-most-shots-against-clinching-games-series': GOALIE_ELIMINATION_GAME_SERIES_COLS,
    'goalie-most-saves-clinching-game': GOALIE_ELIMINATION_GAME_COLS,
    'goalie-most-shots-against-clinching-game': GOALIE_ELIMINATION_GAME_COLS,
    'goalie-shutouts-clinching-game': GOALIE_ELIMINATION_GAME_SHUTOUT_COLS,

    'scf-goalie-most-stanley-cups-won-career': [
        ...BaseColumnDefinitions.PLAYER_TEAMS_BASE_COLS,
        { ...BaseColumnDefinitions.CUPS_WON_COL, Header: 'Stanley Cups' },
        BaseColumnDefinitions.SEASONS_WON_COL,
    ],
    'scf-goalie-most-consecutive-stanley-cups-won': [
        ...BaseColumnDefinitions.PLAYER_TEAMS_BASE_COLS,
        { ...BaseColumnDefinitions.CUPS_WON_COL, Header: 'Stanley Cups' },
        { ...BaseColumnDefinitions.LONGEST_STREAK_COL, Header: 'Consecutive Stanley Cups' },
        { ...BaseColumnDefinitions.STREAK_DESC_COL, Header: 'Streak Seasons' },
    ],
    'goalie-most-stanley-cup-final-appearances-career': [
        ...BaseColumnDefinitions.PLAYER_TEAMS_BASE_COLS,
        BaseColumnDefinitions.APPEARANCES_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
        BaseColumnDefinitions.STANLEY_CUPS_COL,
    ],
    'goalie-most-consecutive-stanley-cup-final-appearances': [
        ...BaseColumnDefinitions.PLAYER_TEAMS_BASE_COLS,
        { ...BaseColumnDefinitions.APPEARANCES_COL, Header: 'Total Appearances' },
        BaseColumnDefinitions.CONSEC_SEASONS_COL,
        BaseColumnDefinitions.STREAK_SEASONS_COL,
    ],
    'goalie-most-stanley-cup-final-games-played-career': [
        ...BaseColumnDefinitions.PLAYER_TEAMS_BASE_COLS,
        BaseColumnDefinitions.APPEARANCES_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
        BaseColumnDefinitions.STANLEY_CUPS_COL,
    ],

    'goalie-most-wins-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
        BaseColumnDefinitions.WIN_PCTG_COL,
    ],
    'goalie-longest-win-streaks-multiple-stanley-cup-final-series': [
        ...BaseColumnDefinitions.PLAYER_TEAMS_BASE_COLS,
        { ...BaseColumnDefinitions.WIN_STREAK_COL, Header: 'Winning Streak' },
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],

    'goalie-gaa-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
        SAVES,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
        BaseColumnDefinitions.TIME_ON_ICE_COL,
        GOALS_AGAINST_AVERAGE_COL,
    ],
    'goalie-gaa-stanley-cup-final-series': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        BaseColumnDefinitions.OPPONENT_COL,
        ...GOALIE_SCF_SERIES_MODIFIED_WIDTH_COLS,
        { ...BaseColumnDefinitions.TIME_ON_ICE_COL, minWidth: 75 },
        { ...GOALS_AGAINST_AVERAGE_COL, minWidth: 75 },
    ],

    'goalie-save-percentage-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
        SAVES,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
        SAVE_PCTG_COL,
    ],
    'goalie-save-percentage-stanley-cup-final-series': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        BaseColumnDefinitions.OPPONENT_COL,
        ...GOALIE_SCF_SERIES_MODIFIED_WIDTH_COLS,
        { ...SAVE_PCTG_COL, minWidth: 75 },
    ],

    'goalie-most-shutouts-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
        BaseColumnDefinitions.SHUTOUTS_COL,
    ],
    'goalie-most-shutouts-stanley-cup-final-series': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        BaseColumnDefinitions.OPPONENT_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
        BaseColumnDefinitions.SHUTOUTS_COL,
    ],
    'goalie-most-consecutive-shutouts-stanley-cup-final-series': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        BaseColumnDefinitions.OPPONENT_COL,
        SHUTOUT_STREAK_COL,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],
    'goalie-longest-shutout-sequence-stanley-cup-final-series': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        BaseColumnDefinitions.OPPONENT_COL,
        { ...DURATION_SECONDS, Header: 'Shutout Sequence' },
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],
    'goalie-longest-shutout-sequence-multiple-stanley-cup-final-series': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        { ...DURATION_SECONDS, Header: 'Shutout Sequence' },
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],

    'goalie-most-saves-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
        SAVES,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
        SAVE_PCTG_COL,
    ],
    'goalie-most-saves-stanley-cup-final-series': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        BaseColumnDefinitions.OPPONENT_COL,
        ...GOALIE_SCF_SERIES_MODIFIED_WIDTH_COLS,
        { ...SAVE_PCTG_COL, minWidth: 75 },
    ],
    'goalie-most-saves-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_GOALIE_GAME_COLS,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
        SAVES,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
    ],
    'goalie-most-saves-stanley-cup-final-period': [
        ...BaseColumnDefinitions.SCF_GOALIE_GAME_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
        SAVES,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
    ],

    'goalie-most-shot-against-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        ...BaseColumnDefinitions.WIN_LOSS_TIE_COLS,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
        SAVES,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
        SAVE_PCTG_COL,
    ],
    'goalie-most-shot-against-stanley-cup-final-series': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        BaseColumnDefinitions.OPPONENT_COL,
        ...GOALIE_SCF_SERIES_MODIFIED_WIDTH_COLS,
        { ...SAVE_PCTG_COL, minWidth: 75 },
    ],
    'goalie-most-shot-against-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_GOALIE_GAME_COLS,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
        SAVES,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
    ],
    'goalie-most-shot-against-stanley-cup-final-period': [
        ...BaseColumnDefinitions.SCF_GOALIE_GAME_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
        SAVES,
        BaseColumnDefinitions.GOALS_AGAINST_COL,
    ],

    'goalie-most-overtime-games-played-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        ...BaseColumnDefinitions.OT_RECORD_COLS,
    ],
    'goalie-most-overtime-wins-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        ...BaseColumnDefinitions.OT_RECORD_COLS,
    ],
    'goalie-most-overtime-saves-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        BaseColumnDefinitions.OVERTIME_WINS_COL,
        BaseColumnDefinitions.OVERTIME_LOSSES_COL,
        BaseColumnDefinitions.OVERTIME_TIES_COL,
        OT_SHOTS_AGAINST,
        OT_SAVES,
        OT_GOALS_AGAINST,
        OT_SAVE_PERCENTAGE,
    ],
    'goalie-most-overtime-saves-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_GOALIE_GAME_COLS,
        OT_SHOTS_AGAINST,
        OT_SAVES,
    ],
    'goalie-most-overtime-saves-stanley-cup-final-period': [
        ...BaseColumnDefinitions.SCF_GOALIE_GAME_COLS,
        BaseColumnDefinitions.OT_PERIOD_COL,
        BaseColumnDefinitions.SHOTS_AGAINST_COL,
        SAVES,
    ],
    ...goaltenderInternationalColumns,
};
